import { useEffect, useMemo, useState } from "react";
import { RelayPool } from "applesauce-relay";
import { toArray, lastValueFrom } from "rxjs";
import { parseNsiteHostname, getFallbackNsiteContext, type NsiteContext } from "@/lib/nsite";

/** Minimal shape of a Nostr event for our purposes. */
interface NostrEvent {
  id: string;
  pubkey: string;
  created_at: number;
  kind: number;
  tags: string[][];
  content: string;
  sig: string;
}

/** Relays to query for snapshot events. */
const SNAPSHOT_RELAYS = [
  "wss://relay.damus.io",
  "wss://relay.primal.net",
  "wss://relay.ditto.pub",
  "wss://nos.lol",
];

/**
 * Fetch all kind 5128 manifest snapshot events for the current nsite.
 *
 * Parses the current hostname to determine the site's identity, then queries
 * relays for snapshot events whose `a` tag references this site's manifest.
 *
 * @param pool  The app's shared RelayPool instance.
 * @returns     `{ snapshots, context, loading, error }`
 */
export function useNsiteSnapshots(pool: RelayPool) {
  const context = useMemo<NsiteContext>(
    () => parseNsiteHostname(window.location.hostname) ?? getFallbackNsiteContext(),
    [],
  );

  const [snapshots, setSnapshots] = useState<NostrEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build the `a` tag value that snapshots reference.
  const aTagValue = useMemo(() => {
    if (!context) return null;
    switch (context.type) {
      case "root":
        return `15128:${context.pubkey}:`;
      case "named":
        return `35128:${context.pubkey}:${context.dTag}`;
      case "snapshot":
        // If we're already viewing a snapshot, we can still find siblings
        // by looking at the snapshot's own `a` tag — but we'd need a
        // two-step fetch. For now, return null.
        return null;
      default:
        return null;
    }
  }, [context]);

  useEffect(() => {
    if (!aTagValue) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const events = await lastValueFrom(
          pool
            .request(SNAPSHOT_RELAYS, { kinds: [5128], "#a": [aTagValue], limit: 50 })
            .pipe(toArray()),
        );

        if (cancelled) return;

        // Deduplicate by event id and sort newest first
        const seen = new Set<string>();
        const unique: NostrEvent[] = [];
        for (const ev of events as NostrEvent[]) {
          if (!seen.has(ev.id)) {
            seen.add(ev.id);
            unique.push(ev);
          }
        }
        unique.sort((a, b) => b.created_at - a.created_at);
        setSnapshots(unique);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to fetch snapshots");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pool, aTagValue]);

  return { snapshots, context, loading, error };
}
